import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

const formData = {
  id: 0,
  designId: "",
  designName: "",
  isPercentage: true,
  value: 0,
};
const typeOptions = [
  { label: "Percentage", value: true },
  { label: "Fixed Amount (LKR)", value: false },
];

const DesignDiscountDialog = ({ discount, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ ...formData });
  const [errors, setErrors] = useState({});
  const toast = useRef(null);

  useEffect(() => {
    setForm(discount ? { ...formData, ...discount } : { ...formData });
  }, [discount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.designId || !String(form.designId).trim())
      newErrors.designId = "Design ID is required (Firestore document id from gallery)";
    if (form.value === null || form.value === undefined || form.value < 0)
      newErrors.value = "Value is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const payload = {
        id: form.id || 0,
        designId: String(form.designId).trim(),
        designName: String(form.designName || "").trim() || null,
        isPercentage: form.isPercentage !== false,
        value: form.value ?? 0,
      };
      const result = await axios.post("/api/designDiscounts", payload);
      if (result.data?.id) {
        toast.current.show({
          severity: "success",
          summary: "Discount saved",
          detail: "Design discount has been added/updated.",
          life: 3000,
        });
        setForm({ ...formData });
        onSubmit();
      }
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.title || "Failed to save design discount.",
        life: 3000,
      });
    }
  };

  return (
    <div>
      <Toast ref={toast} position="bottom-right" />
      <form onSubmit={handleSubmit}>
        <div className="p-field">
          <label htmlFor="designId">Design ID *</label>
          <InputText
            id="designId"
            name="designId"
            value={form.designId || ""}
            onChange={handleChange}
            placeholder="Firestore design document id (e.g. from gallery item URL)"
            autoComplete="off"
          />
          {errors.designId && (
            <label className="p-error">{errors.designId}</label>
          )}
        </div>
        <div className="p-field">
          <label htmlFor="designName">Design name (optional)</label>
          <InputText
            id="designName"
            name="designName"
            value={form.designName || ""}
            onChange={handleChange}
            placeholder="Display name for reference"
            autoComplete="off"
          />
        </div>
        <div className="p-field">
          <label>Price per meter</label>
          <p className="text-color-secondary text-sm mt-1 mb-2">
            Managed in the gallery (Firestore). This form sets only the discount for this design.
          </p>
        </div>
        <div className="p-field">
          <label className="block font-semibold mb-2">Discount</label>
          <p className="text-color-secondary text-sm mt-0 mb-2">
            Shown as a bubble on the gallery item and applied in cart.
          </p>
          <label className="block mt-2">Type *</label>
          <Dropdown
            value={form.isPercentage}
            options={typeOptions}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, isPercentage: e.value }))
            }
            placeholder="Select type"
            className="mt-1"
          />
          <label className="block mt-2" htmlFor="value">Value *</label>
          <InputNumber
            id="value"
            value={form.value}
            onValueChange={(e) =>
              setForm((prev) => ({ ...prev, value: e.value }))
            }
            min={0}
            className="mt-1"
          />
          {errors.value && <label className="p-error block">{errors.value}</label>}
        </div>
        <div className="flex justify-content-end mt-3">
          <Button type="button" label="Cancel" className="p-button-text mr-2" onClick={onCancel} />
          <Button type="submit" label="Submit" />
        </div>
      </form>
    </div>
  );
};

export default DesignDiscountDialog;
